<script setup lang="ts">
import api from '@/api';
import type { ExpenseTypeDto } from '@/api/backend/public';
import { useDeclarationProcess } from '@/helpers/useDeclarationProcess';
import { useApplicantPermissions } from '@/helpers/useApplicantPermissions';
import type { ApplicantId } from '@/types';
import { useTemplateRefsList } from '@vueuse/core';
import { cloneDeep } from 'lodash-es';
import { onBeforeMount, ref, computed } from 'vue';
import ExpensesForm from './modules/Expenses/ExpensesForm.vue';
import { getFullName } from '@/helpers/format';

const { loanApp, loanApplicants, saveAndGoNext, goBack } = useDeclarationProcess();
const {
  currentUserPartner,
  getHouseholdApplicantForExpenses,
  getHouseholdExpenseOwnerId,
  canEditHouseholdExpenses,
  isViewingPartnerExpenses,
  isBrokerUser,
} = useApplicantPermissions();

const isReady = ref(false);
const expenseTypes = ref<ExpenseTypeDto[]>([]);

// Use permissions composable for household-based applicants (keep as computed refs for reactivity)
const validApplicants = getHouseholdApplicantForExpenses;
const expenseOwnerId = getHouseholdExpenseOwnerId;

const financials = ref(cloneDeep(loanApp.value.financials));
const expenseRefs = useTemplateRefsList<InstanceType<typeof ExpensesForm>>();

const activeTab = ref<ApplicantId | undefined>(
  validApplicants.value.length === 1 ? validApplicants.value[0] : undefined,
);

const isCompleted = (id: ApplicantId) => financials.value[id]?.expenses?.numberOfDependants !== undefined;

// Check if current user can edit expenses
const canEdit = computed(() => canEditHouseholdExpenses.value);

// Partner name for display in messages
const partnerName = computed(() => {
  if (!currentUserPartner.value) return '';
  return currentUserPartner.value.preferredName || currentUserPartner.value.firstName;
});

const validateExpenses = async () => {
  // Don't validate if viewing partner's expenses (read-only)
  if (isViewingPartnerExpenses.value) return true;

  // Don't validate if user cannot edit
  if (!canEdit.value) return true;

  let isValid = true;
  const refCount = expenseRefs.value.length;
  for (let i = 0; i < refCount; i++) {
    isValid = (await expenseRefs.value[i].update()) ?? false;
    if (!isValid) {
      activeTab.value = validApplicants.value[i];
      window.scrollTo(0, 0);
      break;
    }
  }
  return isValid;
};

const saveAndContinue = async () => {
  if (await validateExpenses()) {
    saveAndGoNext({
      financials: financials.value,
    });
  }
};

onBeforeMount(async () => {
  isReady.value = false;
  try {
    const expenseTypesResponse = await api.backend.public.listExpenseTypes(loanApp.value.structuralVersionNumber);
    if (expenseTypesResponse.expenseTypes?.length) {
      expenseTypesResponse.expenseTypes.sort((a, b) => {
        const sortOrderA = a.sortOrder || 0;
        const sortOrderB = b.sortOrder || 0;
        return sortOrderA - sortOrderB;
      });
      expenseTypes.value = expenseTypesResponse.expenseTypes;
    }
  } finally {
    isReady.value = true;
  }
});
</script>

<template>
  <div v-if="isReady" class="w-full">
    <div class="mb-4 pb-2">
      <h2 class="text-2xl font-medium">
        What are your household
        <BxHighlight>expenses</BxHighlight>?
      </h2>
    </div>

    <!-- Contextual messages for multi-applicant scenarios -->
    <div v-if="loanApp.applicants.length > 1" class="my-6">
      <!-- Message when viewing partner's already-submitted expenses (view-only) -->
      <p v-if="isViewingPartnerExpenses">
        Please review your combined household expenses with {{ partnerName }}. If you wish to make any changes, please
        contact us.
      </p>
      <!-- Message when married and can edit (first submitter) -->
      <p v-else-if="currentUserPartner && canEdit">
        Please include your combined household expenses including {{ partnerName }}.
      </p>
      <!-- Message for non-married applicants in multi-applicant scenario -->
      <p v-else-if="!currentUserPartner && loanApp.actionType === 'createNew' && !isBrokerUser">
        Only include your expenses, we will request this information from other applicants via email.
      </p>
    </div>

    <div class="my-6 grid gap-4">
      <div v-for="applicantId in validApplicants" :key="applicantId">
        <!-- Expanded Form View -->
        <BxCard :class="applicantId !== activeTab && 'hidden'">
          <h2 class="mb-4 text-xl font-medium">
            {{ getFullName(loanApplicants[applicantId]) }}
            <span v-if="!canEdit" class="ml-2 text-sm font-normal text-gray-500"> (View only) </span>
          </h2>
          <!-- For married couples, use the expense owner's financials -->
          <ExpensesForm
            v-if="currentUserPartner"
            :ref="expenseRefs.set"
            v-model="financials[expenseOwnerId].expenses"
            :applicant-id="applicantId"
            :animate="applicantId === activeTab"
            :expense-types="expenseTypes"
            :show-buttons="validApplicants.length > 1"
            :disabled="!canEdit"
            @save="activeTab = undefined"
            @cancel="activeTab = undefined"
          />
          <!-- For single applicants, use their own financials -->
          <ExpensesForm
            v-else
            :ref="expenseRefs.set"
            v-model="financials[applicantId].expenses"
            :applicant-id="applicantId"
            :animate="applicantId === activeTab"
            :expense-types="expenseTypes"
            :show-buttons="validApplicants.length > 1"
            :disabled="!canEdit"
            @save="activeTab = undefined"
            @cancel="activeTab = undefined"
          />
        </BxCard>

        <!-- Collapsed Card View -->
        <BxCard :class="applicantId === activeTab && 'hidden'">
          <div class="flex items-center">
            <div class="card-details">
              <div class="font-medium">
                <span>{{ getFullName(loanApplicants[applicantId]) }}</span>
                <span v-if="!canEdit" class="ml-2 text-sm font-normal text-gray-500">(View only)</span>
                <span class="ml-6 font-normal text-gray-400">
                  ({{ isCompleted(currentUserPartner ? expenseOwnerId : applicantId) ? 'completed' : 'incomplete' }})
                </span>
              </div>
            </div>
            <div class="card-button">
              <!-- Show edit button if user can edit -->
              <BxButton
                v-if="canEdit"
                variant="secondary"
                :disabled="activeTab && activeTab !== applicantId"
                @click="activeTab = applicantId"
              >
                {{ isCompleted(currentUserPartner ? expenseOwnerId : applicantId) ? 'Edit' : 'Add' }}
                expenses
              </BxButton>
              <!-- Show view button if user cannot edit (partner already submitted) -->
              <BxButton
                v-else
                variant="secondary"
                :disabled="activeTab && activeTab !== applicantId"
                @click="activeTab = applicantId"
              >
                View expenses
              </BxButton>
            </div>
          </div>
        </BxCard>
      </div>
    </div>

    <div class="mt-6">
      <BxButton id="expenses-continue" variant="primary" @click="saveAndContinue()">Save and continue</BxButton>
      <BxButton variant="link" class="ml-4" @click="goBack()">Back</BxButton>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.card-details {
  flex-grow: 1;
}

.card-button {
  flex-basis: 200px;

  text-align: right;
}
</style>
